import * as React from "react";
import { GenericTextInput } from "./inputs/GenericTextInput";
import { HRQuestionWithStatus } from "../models/HR";
import { HRAvailabilityPlaceholderSettings, HRQuestionId } from "../messages";
import { HRQuestion, HRAnswerResult, HRDownloadableFilePreviewResponse } from "../messages/HR";
import assertNever from "assert-never";
import { GenericRadioInput } from "./inputs/GenericRadioInput";
import { GenericCheckBoxInput } from "./inputs/GenericCheckBoxInput";
import { GenericMaskedInput } from "./inputs/GenericMaskedInput";
import { GenericDateInput } from "./inputs/GenericDateInput";
import { GenericNumericInput } from "./inputs/GenericNumericInput";
import { GenericDropDownInput } from "./inputs/GenericDropDownInput";
import { GenericSignatureInput } from "./inputs/GenericSignatureInput";
import { GenericAddressInput } from "./inputs/GenericAddressInput";
import { GenericDoubleSliderInput } from "./inputs/GenericDoubleSliderInput";
import { CaregiverAvailabilityActivity } from "../activities/CaregiverAvailabillityActivity";
import { API } from "../API";
import { GenericStarInput } from "./inputs/GenericStarInput";
import { PlaceholderEmbeddedPDF } from "./placeholders/PlaceholderEmbeddedPDF";
import { PlaceholderText } from "./placeholders/PlaceholderText";
import { GenericDollarInput } from "./inputs/GenericDollarInput";
import { GenericDocumentInput } from "./inputs/GenericDocumentInput";
import { GenericDocumentWithExpiryInput } from "./inputs/GenericDocumentWithExpiryInput";
import { HRQuestionTemplateId } from "../messages/ObjectIds";
import { checkMobile } from "../Utils";
import { HRCertificationPayRates, HRCertificationPayRate, CertificationSettings, HRCertificationPayRatesWithSupplementals, PayRateSupplemental } from "../messages/Agency";
import { Dialog } from "@blueprintjs/core";
import { GenericInputHeader } from "./inputs/GenericInputHeader";
import { GenericGeneralSignature } from "./inputs/GenericGeneralSignature";
import { PlaceholderFormattedText } from "./placeholders/PlaceholderFormattedText";
import { PlaceholderDownloadableFile } from "./placeholders/PlaceholderDownloadableFile";

interface GenericQuestionGroupProps {
    questions: HRQuestionWithStatus<HRQuestion>[];
    // TODO give type to newval
    onAnswerChange: (newVal: any, questionId: HRQuestionId) => void;
    onPayRatesChange: (rates: HRCertificationPayRatesWithSupplementals) => Promise<HRAnswerResult>;
    getCertificationPayRates: () => CertificationSettings;
    onAnswerBlur: (questionId: HRQuestionId) => void;
    onTriggerEvaluateHierarchyStatus: (questionId: HRQuestionId) => void;
    availabilitySettings: HRAvailabilityPlaceholderSettings | null;
    api: API;
}

interface GenericQuestionGroupState {
    forMobile: boolean;
    payRateSelectedCountyGroupName: string | null;
    showPayRateSelectCountyFirstDialog: boolean;
}

export class GenericQuestionGroup extends React.Component<GenericQuestionGroupProps, GenericQuestionGroupState> {
    private originalPayRateQuestionText: string;
    private triggerDialog: boolean;

    constructor(props: GenericQuestionGroupProps) {
        super(props);
        const forMobile = checkMobile(navigator.userAgent || navigator.vendor || (window as any).opera);
        this.state = {
            forMobile,
            payRateSelectedCountyGroupName: null,
            showPayRateSelectCountyFirstDialog: false
        };
        this.originalPayRateQuestionText = "";
        this.triggerDialog = true;
    }

    getCertificationPayRates(): HRCertificationPayRate[] {
        return this.props.getCertificationPayRates().locationPayRates;
    }

    withDefault (val: any, defaultValue: string): string {
        if (typeof(val) === "string") {
            return val === "" ? defaultValue : val;
        } else if (typeof(val) === "number") {
            return val.toString();
        }
        return defaultValue;
    }

    parsePayRatesToDisplayText (question: HRQuestion.CheckboxQuestion, rates: HRCertificationPayRates | null, supplementals: PayRateSupplemental[]): string {
        if (this.originalPayRateQuestionText === "") {
            this.originalPayRateQuestionText = question.questionText;
            this.triggerDialog = false;
            question.chosenAnswerIds = [];
            return "";
        }
        if (rates === null) {
            return "";
        }
        let tempText: string = this.originalPayRateQuestionText.valueOf();

        const unspecified = `<i style="opacity: 0.5;">Unspecified</i>`;
        tempText = tempText.replace(new RegExp("{hourly_pay_rate}", "g"), this.withDefault(rates.hourlyRate.toString(), unspecified));
        tempText = tempText.replace(new RegExp("{live_in_pay_rate}", "g"), this.withDefault(rates.dailyRate.toString(), unspecified));
        tempText = tempText.replace(new RegExp("{over_time_pay_rate}", "g"), this.withDefault(rates.overtimeRate.toString(), unspecified));
        tempText = tempText.replace(new RegExp("{pay_range}", "g"), this.withDefault(rates.payRange, unspecified));
        tempText = tempText.replace(new RegExp("{base_wage}", "g"), this.withDefault(rates.baseWage, unspecified));
        tempText = tempText.replace(new RegExp("{additional_wage}", "g"), this.withDefault(rates.additionalWage, unspecified));
        tempText = tempText.replace(new RegExp("{supplemental_wage}", "g"), this.withDefault(rates.supplementalWage, unspecified));
        tempText = tempText.replace(new RegExp("{wage_overtime_hourly}", "g"), rates.hourlyRate ? (rates.hourlyRate * 1.5).toString() : unspecified);
        if (rates.workShiftLine1Rate !== null) {
            let workShiftLine1 = "$" + rates.workShiftLine1Rate + " per hour";
            if (rates.workShiftLine1Label !== null) {
                workShiftLine1 += " for " + rates.workShiftLine1Label;
            }
            tempText = tempText.replace(new RegExp("{work_shift_line1}", "g"), workShiftLine1);
        }
        if (rates.workShiftLine2Rate !== null) {
            let workShiftLine2 = "$" + rates.workShiftLine2Rate + " per hour";
            if (rates.workShiftLine1Label !== null) {
                workShiftLine2 += " for " + rates.workShiftLine2Label;
            }
            tempText = tempText.replace(new RegExp("{work_shift_line2}", "g"), workShiftLine2);
        }
        if (supplementals.length > 0) {
            tempText = tempText.replace(new RegExp("{supplement_1_id}", "g"), "1");
            tempText = tempText.replace(new RegExp("{supplement_1_hourly_rate}", "g"), this.withDefault("$" + supplementals[0].hourlyRate, unspecified));
            tempText = tempText.replace(new RegExp("{supplement_1_type}", "g"), this.withDefault(supplementals[0].type, unspecified));
            tempText = tempText.replace(new RegExp("{supplement_1_agency_name}", "g"), this.withDefault(supplementals[0].agencyName, unspecified));
            tempText = tempText.replace(new RegExp("{supplement_1_agency_description}", "g"), this.withDefault(supplementals[0].agencyDescription, unspecified));
            if (supplementals.length > 1) {
                tempText = tempText.replace(new RegExp("{supplement_2_id}", "g"), "2");
                tempText = tempText.replace(new RegExp("{supplement_2_hourly_rate}", "g"), this.withDefault("$" + supplementals[1].hourlyRate.toString(), unspecified));
                tempText = tempText.replace(new RegExp("{supplement_2_type}", "g"), this.withDefault(supplementals[1].type, unspecified));
                tempText = tempText.replace(new RegExp("{supplement_2_agency_name}", "g"), this.withDefault(supplementals[1].agencyName, unspecified));
                tempText = tempText.replace(new RegExp("{supplement_2_agency_description}", "g"), this.withDefault(supplementals[1].agencyDescription, unspecified));
                if (supplementals.length > 2) {
                    tempText = tempText.replace(new RegExp("{supplement_3_id}", "g"), "3");
                    tempText = tempText.replace(new RegExp("{supplement_3_hourly_rate}", "g"), this.withDefault("$" + supplementals[2].hourlyRate.toString(), unspecified));
                    tempText = tempText.replace(new RegExp("{supplement_3_type}", "g"), this.withDefault(supplementals[2].type, unspecified));
                    tempText = tempText.replace(new RegExp("{supplement_3_agency_name}", "g"), this.withDefault(supplementals[2].agencyName, unspecified));
                    tempText = tempText.replace(new RegExp("{supplement_3_agency_description}", "g"), this.withDefault(supplementals[2].agencyDescription, unspecified));
                } else {
                    tempText = tempText.replace(new RegExp("{supplement_3_id}", "g"), "");
                    tempText = tempText.replace(new RegExp("{supplement_3_hourly_rate}", "g"), "");
                    tempText = tempText.replace(new RegExp("{supplement_3_type}", "g"), "");
                    tempText = tempText.replace(new RegExp("{supplement_3_agency_name}", "g"), "");
                    tempText = tempText.replace(new RegExp("{supplement_3_agency_description}", "g"), "");
                }
            } else {
                tempText = tempText.replace(new RegExp("{supplement_2_id}", "g"), "");
                tempText = tempText.replace(new RegExp("{supplement_2_hourly_rate}", "g"), "");
                tempText = tempText.replace(new RegExp("{supplement_2_type}", "g"), "");
                tempText = tempText.replace(new RegExp("{supplement_2_agency_name}", "g"), "");
                tempText = tempText.replace(new RegExp("{supplement_2_agency_description}", "g"), "");
                tempText = tempText.replace(new RegExp("{supplement_3_id}", "g"), "");
                tempText = tempText.replace(new RegExp("{supplement_3_hourly_rate}", "g"), "");
                tempText = tempText.replace(new RegExp("{supplement_3_type}", "g"), "");
                tempText = tempText.replace(new RegExp("{supplement_3_agency_name}", "g"), "");
                tempText = tempText.replace(new RegExp("{supplement_3_agency_description}", "g"), "");
            }
        } else {
            tempText = tempText.replace(new RegExp("{supplement_1_id}", "g"), "");
            tempText = tempText.replace(new RegExp("{supplement_1_hourly_rate}", "g"), "");
            tempText = tempText.replace(new RegExp("{supplement_1_type}", "g"), "");
            tempText = tempText.replace(new RegExp("{supplement_1_agency_name}", "g"), "");
            tempText = tempText.replace(new RegExp("{supplement_1_agency_description}", "g"), "");
            tempText = tempText.replace(new RegExp("{supplement_2_id}", "g"), "");
            tempText = tempText.replace(new RegExp("{supplement_2_hourly_rate}", "g"), "");
            tempText = tempText.replace(new RegExp("{supplement_2_type}", "g"), "");
            tempText = tempText.replace(new RegExp("{supplement_2_agency_name}", "g"), "");
            tempText = tempText.replace(new RegExp("{supplement_2_agency_description}", "g"), "");
            tempText = tempText.replace(new RegExp("{supplement_3_id}", "g"), "");
            tempText = tempText.replace(new RegExp("{supplement_3_hourly_rate}", "g"), "");
            tempText = tempText.replace(new RegExp("{supplement_3_type}", "g"), "");
            tempText = tempText.replace(new RegExp("{supplement_3_agency_name}", "g"), "");
            tempText = tempText.replace(new RegExp("{supplement_3_agency_description}", "g"), "");
        }

        return tempText;
    }

    onPayRatesSelect (selectedCountyGroupName: string, q: HRQuestion.CheckboxQuestion) {
        let selectedCountyGroup: HRCertificationPayRate | undefined;
        if (selectedCountyGroupName && selectedCountyGroupName.length) {
            selectedCountyGroup = this.getCertificationPayRates().find((group: HRCertificationPayRate) => group.locationGroup === selectedCountyGroupName);
        }
        if (selectedCountyGroup === undefined) {
            this.setState({ payRateSelectedCountyGroupName: null });
            if (q.chosenAnswerIds !== null && q.chosenAnswerIds.length > 0) {
                this.props.onAnswerChange(1, q.id);
                this.props.onAnswerBlur(q.id);
            }
            return;
        }
        this.props.onPayRatesChange({
            ...selectedCountyGroup.rates,
            supplementals: selectedCountyGroup.supplementals
        }).then(result => {
            if (result.isSuccessful) {
                this.setState({ payRateSelectedCountyGroupName: selectedCountyGroupName });
            }
        });
    }

    onPayRateCheck (newVal: any, id: HRQuestionId) {
        if (!this.triggerDialog) {
            this.triggerDialog = true;
            return;
        }
        if (this.state.payRateSelectedCountyGroupName) {
            this.props.onAnswerChange(newVal, id);
            this.setState({showPayRateSelectCountyFirstDialog: false});
        } else {
            this.setState({showPayRateSelectCountyFirstDialog: true});
        }
    }

    getCountyGroupName (countyGroup: HRCertificationPayRate) {
        return countyGroup.locationGroup;
    }

    getDownloadableFile(hrQuestion: HRQuestion): Promise<HRDownloadableFilePreviewResponse> {
        return this.props.api.getDownloadableFile(hrQuestion);
    }

    renderPayRateCheckQuestion (q: HRQuestion.CheckboxQuestion, question: HRQuestionWithStatus<HRQuestion>): JSX.Element {
        const selectedCountyGroup = this.getCertificationPayRates().find((group: HRCertificationPayRate) => group.locationGroup === this.state.payRateSelectedCountyGroupName);
        const rates: HRCertificationPayRates | null = selectedCountyGroup ? selectedCountyGroup.rates : null;
        const supplementals: PayRateSupplemental[] = selectedCountyGroup ? selectedCountyGroup.supplementals : [];
        q.questionText = this.parsePayRatesToDisplayText(q as HRQuestion.CheckboxQuestion, rates, supplementals);

        return (
            <div key={"PayRateQuestion-" + HRQuestionId.unwrap(q.id)}>
                <div>Please select your location and confirm</div>
                <br />
                <select
                    required={true}
                    className={"form-control bp3-control"}
                    onChange={(e) => this.onPayRatesSelect(e.target.value, q)}
                >
                    <option value="">Select your location</option>
                    {this.getCertificationPayRates().map((countyGroup: HRCertificationPayRate, index: number) => {
                        return <option key={index} value={countyGroup.locationGroup}>
                            {this.getCountyGroupName(countyGroup)}
                        </option>;
                    })}
                </select>
                <GenericCheckBoxInput
                    key={HRQuestionId.unwrap(q.id)}
                    hrCheckBoxQuestionWithStatus={{
                        question: q,
                        status: question.status
                    }}
                    isHidden={q.isHidden}
                    onChange={(newVal, id) => this.onPayRateCheck(newVal, id)}
                    onBlur={(id) => this.props.onAnswerBlur(id)}
                />
                <Dialog
                    isOpen={this.state.showPayRateSelectCountyFirstDialog}
                    isCloseButtonShown={true}
                    title={"Error"}
                    canOutsideClickClose={true}
                    canEscapeKeyClose={true}
                    onClose={() => this.setState({showPayRateSelectCountyFirstDialog: false})}
                >
                    <div className="bp3-dialog-body">
                        Please first choose your county and then check to confirm
                    </div>
                </Dialog>
            </div>
        );
    }

    render() {
        return (
            <div>
                <div>
                    {this.props.questions.map((question: HRQuestionWithStatus<HRQuestion>) => {
                        const q = question.question;
                        switch (q.type) {
                            case "Email":
                            case "Text": {
                                return (<GenericTextInput
                                    key={HRQuestionId.unwrap(q.id)}
                                    hrTextQuestionWithStatus={{
                                        question: q,
                                        status: question.status
                                    }}
                                    isHidden={q.isHidden}
                                    onChange={(newVal, id) => this.props.onAnswerChange(newVal, id)}
                                    onBlur={(id) => this.props.onAnswerBlur(id)}
                                />);
                            }
                            case "FreeText":
                            case "Checkbox": {
                                const payRateCheckBoxesArr = ["PayRateCheck", "PayRateCheckSingle", "PayRateHourlyCheck", "LS55SPayRateCheck", "LS56PayRateCheckSingle", "LS57PayRateCheckSingle", "LS54SPayRateCheckSingle", "LS54RPayRateCheckSingle", "LS54HCPayRateCheckSingle", "LS59PayRateCheckSingle", "LS51PayRateCheckSingle", "LS62PayRateCheck"];
                                if (payRateCheckBoxesArr.indexOf(q.textId.toString()) !== -1) {
                                    return this.renderPayRateCheckQuestion(q as HRQuestion.CheckboxQuestion, question);
                                }
                                return (<GenericCheckBoxInput
                                    key={HRQuestionId.unwrap(q.id)}
                                    hrCheckBoxQuestionWithStatus={{
                                        question: q,
                                        status: question.status
                                    }}
                                    isHidden={q.isHidden}
                                    onChange={(newVal, id) => this.props.onAnswerChange(newVal, id)}
                                    onBlur={(id) => this.props.onAnswerBlur(id)}
                                />);
                            }
                            case "AmericanTest":
                            case "Radio": {
                                return (<GenericRadioInput
                                    key={HRQuestionId.unwrap(q.id)}
                                    hrRadioQuestionWithStatus={{
                                        question: q,
                                        status: question.status
                                    }}
                                    isHidden={q.isHidden}
                                    onChange={(newVal, id) => this.props.onAnswerChange(newVal, id)}
                                    onBlur={(id) => this.props.onAnswerBlur(id)}
                                />);
                            }
                            case "DropDown": {
                                return (<GenericDropDownInput
                                    key={HRQuestionId.unwrap(q.id)}
                                    hrDropDownQuestionWithStatus={{
                                        question: q,
                                        status: question.status
                                    }}
                                    isHidden={q.isHidden}
                                    onChange={(newVal, id) => this.props.onAnswerChange(newVal, id)}
                                    onBlur={(id) => this.props.onAnswerBlur(id)}
                                />);
                            }
                            case "Numeric": {
                                return (<GenericNumericInput
                                    key={HRQuestionId.unwrap(q.id)}
                                    hrNumericQuestionWithStatus={{
                                        question: q,
                                        status: question.status
                                    }}
                                    isHidden={q.isHidden}
                                    onChange={(newVal, id) => this.props.onAnswerChange(newVal, id)}
                                    onBlur={(id) => this.props.onAnswerBlur(id)}
                                />);
                            }
                            case "SSN":
                            case "PhoneNumber": {
                                return (<GenericMaskedInput
                                    key={HRQuestionId.unwrap(q.id)}
                                    hrMaskedQuestion={{
                                        question: q,
                                        status: question.status
                                    }}
                                    isHidden={q.isHidden}
                                    onChange={(newVal, id) => this.props.onAnswerChange(newVal, id)}
                                    onBlur={(id) => this.props.onAnswerBlur(id)}
                                />);
                            }
                            case "BirthDate":
                            case "FullDate":
                            case "MonthYearDate":
                            case "YearDate": {
                                return (<GenericDateInput
                                    key={HRQuestionId.unwrap(q.id)}
                                    hrDateQuestion={{
                                        question: q,
                                        status: question.status
                                    }}
                                    isHidden={q.isHidden}
                                    onChange={(newVal, id) => this.props.onAnswerChange(newVal, id)}
                                    onBlur={(id) => this.props.onAnswerBlur(id)}
                                    onMobile={this.state.forMobile}
                                />);
                            }
                            case "Signature": {
                                return (<GenericSignatureInput
                                    key={HRQuestionId.unwrap(q.id)}
                                    hrSignatureQuestionWithStatus={{
                                        question: q,
                                        status: question.status
                                    }}
                                    isHidden={q.isHidden}
                                    onChange={(newVal, id) => this.props.onAnswerChange(newVal, id)}
                                    onBlur={(id) => this.props.onAnswerBlur(id)}
                                />);
                            }
                            case "GeneralSignature": {
                                return (<GenericGeneralSignature
                                    key={HRQuestionId.unwrap(q.id)}
                                    hrGeneralSignatureQuestionWithStatus={{
                                        question: q,
                                        status: question.status
                                    }}
                                    isHidden={q.isHidden}
                                    onChange={(newVal, id) => this.props.onAnswerChange(newVal, id)}
                                    onBlur={(id) => this.props.onAnswerBlur(id)}
                                />);
                            }
                            case "Address": {
                                return (<GenericAddressInput
                                    key={HRQuestionId.unwrap(q.id)}
                                    hrAddressQuestionWithStatus={{
                                        question: q,
                                        status: question.status
                                    }}
                                    isHidden={q.isHidden}
                                    onChange={(newVal, id) => this.props.onAnswerChange(newVal, id)}
                                    onBlur={(id) => this.props.onAnswerBlur(id)}
                                />);
                            }
                            case "StartAndEndTime": {
                                return (<GenericDoubleSliderInput
                                    key={HRQuestionId.unwrap(q.id)}
                                    hrStartAndEndTimeQuestion={{
                                        question: q,
                                        status: question.status
                                    }}
                                    isHidden={q.isHidden}
                                    onChange={(newVal, id) => this.props.onAnswerChange(newVal, id)}
                                    onBlur={(id) => this.props.onAnswerBlur(id)}
                                />);
                            }
                            case "Star": {
                                return (<GenericStarInput
                                    key={HRQuestionId.unwrap(q.id)}
                                    hrStarQuestion={{
                                        question: q,
                                        status: question.status
                                    }}
                                    isHidden={q.isHidden}
                                    onChange={(newVal, id) => this.props.onAnswerChange(newVal, id)}
                                    onBlur={(id) => this.props.onAnswerBlur(id)}
                                />);
                            }
                            case "Dollar": {
                                return (<GenericDollarInput
                                    key={HRQuestionId.unwrap(q.id)}
                                    hrDollarQuestionWithStatus={{
                                        question: q,
                                        status: question.status
                                    }}
                                    isHidden={q.isHidden}
                                    onChange={(newVal, id) => this.props.onAnswerChange(newVal, id)}
                                    onBlur={(id) => this.props.onAnswerBlur(id)}
                                />);
                            }
                            case "Document": {
                                return (<GenericDocumentInput
                                    key={HRQuestionId.unwrap(q.id)}
                                    hrDocumentQuestionWithStatus={{
                                        question: q,
                                        status: question.status
                                    }}
                                    isHidden={q.isHidden}
                                    onChange={(newVal, id) => this.props.onAnswerChange(newVal, id)}
                                    onBlur={(id) => this.props.onAnswerBlur(id)}
                                />);
                            }
                            case "DocumentWithExpiry": {
                                return (<GenericDocumentWithExpiryInput
                                    key={HRQuestionId.unwrap(q.id)}
                                    hrDocumentWithExpiryQuestionWithStatus={{
                                        question: q,
                                        status: question.status
                                    }}
                                    isHidden={q.isHidden}
                                    onChange={(newVal, id) => this.props.onAnswerChange(newVal, id)}
                                    onBlur={(id) => this.props.onAnswerBlur(id)}
                                />);
                            }
                            case "Placeholder": {
                                switch (HRQuestionTemplateId.unwrap(q.textId)) {
                                    case ("PlaceholderAvailability"): {
                                        return (
                                            <CaregiverAvailabilityActivity
                                                api={this.props.api}
                                                key={HRQuestionId.unwrap(q.id)}
                                                settings={this.props.availabilitySettings}
                                            />
                                        );
                                    }
                                    case ("PlaceholderIt2014Form"): {
                                        return (
                                            <PlaceholderEmbeddedPDF
                                                key={HRQuestionId.unwrap(q.id)}
                                                pdfUrl={"/pdf/it2104-2020.pdf"}
                                                containerElementId={"GenericQuestionGroupListRootId"}
                                            />
                                        );
                                    }
                                    case ("PlaceholderW4Form"): {
                                        return (
                                            <PlaceholderEmbeddedPDF
                                                key={HRQuestionId.unwrap(q.id)}
                                                pdfUrl={"/pdf/fw4.pdf"}
                                                containerElementId={"GenericQuestionGroupListRootId"}
                                            />
                                        );
                                    }
                                    case ("PlaceholderW42020Form"): {
                                        return (
                                            <PlaceholderEmbeddedPDF
                                                key={HRQuestionId.unwrap(q.id)}
                                                pdfUrl={"/pdf/fw4-2020.pdf"}
                                                containerElementId={"GenericQuestionGroupListRootId"}
                                            />
                                        );
                                    }
                                    case ("PlaceholderW42021Form"): {
                                        return (
                                            <PlaceholderEmbeddedPDF
                                                key={HRQuestionId.unwrap(q.id)}
                                                pdfUrl={"/pdf/fw4-2021.pdf"}
                                                containerElementId={"GenericQuestionGroupListRootId"}
                                            />
                                        );
                                    }
                                    case ("PlaceholderI9Form"): {
                                        return (
                                            <PlaceholderEmbeddedPDF
                                                key={HRQuestionId.unwrap(q.id)}
                                                pdfUrl={"/pdf/i-9-list.pdf"}
                                                containerElementId={"GenericQuestionGroupListRootId"}
                                            />
                                        );
                                    }
                                    case ("PlaceholderF102Form"): {
                                        return (
                                            <PlaceholderEmbeddedPDF
                                                key={HRQuestionId.unwrap(q.id)}
                                                pdfUrl={"/pdf/f102.pdf"}
                                                containerElementId={"GenericQuestionGroupListRootId"}
                                            />
                                        );
                                    }
                                    case ("PlaceholderF103Form"): {
                                        return (
                                            <PlaceholderEmbeddedPDF
                                                key={HRQuestionId.unwrap(q.id)}
                                                pdfUrl={"/pdf/f103.pdf"}
                                                containerElementId={"GenericQuestionGroupListRootId"}
                                            />
                                        );
                                    }
                                    case ("WSTEHBPlaceholder"): {
                                        return (
                                            <PlaceholderEmbeddedPDF
                                                key={HRQuestionId.unwrap(q.id)}
                                                pdfUrl={"/pdf/westchester_employee_handbook.pdf"}
                                                containerElementId={"GenericQuestionGroupListRootId"}
                                            />
                                        );
                                    }
                                    case ("CBPFFormPlaceholder"): {
                                        return (
                                            <PlaceholderEmbeddedPDF
                                                key={HRQuestionId.unwrap(q.id)}
                                                pdfUrl={"/pdf/commuter-benefits-participation-form.pdf"}
                                                containerElementId={"GenericQuestionGroupListRootId"}
                                            />
                                        );
                                    }
                                    case ("PCAFormPlaceholder"): {
                                        return (
                                            <PlaceholderEmbeddedPDF
                                                key={HRQuestionId.unwrap(q.id)}
                                                pdfUrl={"/pdf/pennsylvania-child-abuse.pdf"}
                                                containerElementId={"GenericQuestionGroupListRootId"}
                                            />
                                        );
                                    }
                                    case ("PlaceholderW9Form"): {
                                        return (
                                            <PlaceholderEmbeddedPDF
                                                key={HRQuestionId.unwrap(q.id)}
                                                pdfUrl={"/pdf/fw9.pdf"}
                                                containerElementId={"GenericQuestionGroupListRootId"}
                                            />
                                        );
                                    }
                                    case ("PlaceholderHIPAA960"): {
                                        return (
                                            <PlaceholderEmbeddedPDF
                                                key={HRQuestionId.unwrap(q.id)}
                                                pdfUrl={"/pdf/hipaa-960.pdf"}
                                                containerElementId={"GenericQuestionGroupListRootId"}
                                            />
                                        );
                                    }
                                    case ("Placeholder195OPWDD"): {
                                        return (
                                            <PlaceholderEmbeddedPDF
                                                key={HRQuestionId.unwrap(q.id)}
                                                pdfUrl={"/pdf/195-OPWDD.pdf"}
                                                containerElementId={"GenericQuestionGroupListRootId"}
                                            />
                                        );
                                    }
                                    case ("PlaceholderPACA"): {
                                        return (
                                            <PlaceholderEmbeddedPDF
                                                key={HRQuestionId.unwrap(q.id)}
                                                pdfUrl={"/pdf/pa-child-abuse-consent-form.pdf"}
                                                containerElementId={"GenericQuestionGroupListRootId"}
                                            />
                                        );
                                    }
                                    case ("PlaceholderLS62"): {
                                        return (
                                            <PlaceholderEmbeddedPDF
                                                key={HRQuestionId.unwrap(q.id)}
                                                pdfUrl={"/pdf/ls62.pdf"}
                                                containerElementId={"GenericQuestionGroupListRootId"}
                                            />
                                        );
                                    }
                                    case ("PlaceholderF88502"): {
                                        return (
                                            <PlaceholderEmbeddedPDF
                                                key={HRQuestionId.unwrap(q.id)}
                                                pdfUrl={"/pdf/f88502.pdf"}
                                                containerElementId={"GenericQuestionGroupListRootId"}
                                            />
                                        );
                                    }
                                    case ("PlaceholderIt21042021Form"): {
                                        return (
                                            <PlaceholderEmbeddedPDF
                                                key={HRQuestionId.unwrap(q.id)}
                                                pdfUrl={"/pdf/it2104-2021.pdf"}
                                                containerElementId={"GenericQuestionGroupListRootId"}
                                            />
                                        );
                                    }
                                    case ("PlaceholderDTF75Form"): {
                                        return (
                                            <PlaceholderEmbeddedPDF
                                                key={HRQuestionId.unwrap(q.id)}
                                                pdfUrl={"/pdf/dtf75.pdf"}
                                                containerElementId={"GenericQuestionGroupListRootId"}
                                            />
                                        );
                                    }
                                    case ("PlaceholderCLGS326Form"): {
                                        return (
                                            <PlaceholderEmbeddedPDF
                                                key={HRQuestionId.unwrap(q.id)}
                                                pdfUrl={"/pdf/clgs326.pdf"}
                                                containerElementId={"GenericQuestionGroupListRootId"}
                                            />
                                        );
                                    }
                                    case ("PlaceholderCHRC2021Form"): {
                                        return (
                                            <PlaceholderEmbeddedPDF
                                                key={HRQuestionId.unwrap(q.id)}
                                                pdfUrl={"/pdf/chrc102-2021.pdf"}
                                                containerElementId={"GenericQuestionGroupListRootId"}
                                            />
                                        );
                                    }
                                    case ("PlaceholderACBSRForm"): {
                                        return (
                                            <PlaceholderEmbeddedPDF
                                                key={HRQuestionId.unwrap(q.id)}
                                                pdfUrl={"/pdf/attestation-of-compliance.pdf"}
                                                containerElementId={"GenericQuestionGroupListRootId"}
                                            />
                                        );
                                    }
                                    case ("PlaceholderAGMCForm"): {
                                        return (
                                            <PlaceholderEmbeddedPDF
                                                key={HRQuestionId.unwrap(q.id)}
                                                pdfUrl={"/pdf/affidavit-of-good-moral-character.pdf"}
                                                containerElementId={"GenericQuestionGroupListRootId"}
                                            />
                                        );
                                    }
                                    case ("PlaceholderCBSCForm"): {
                                        return (
                                            <PlaceholderEmbeddedPDF
                                                key={HRQuestionId.unwrap(q.id)}
                                                pdfUrl={"/pdf/caregiver-background-screening-clearinghouse.pdf"}
                                                containerElementId={"GenericQuestionGroupListRootId"}
                                            />
                                        );
                                    }
                                    case ("PlaceholderCAFForm"): {
                                        return (
                                            <PlaceholderEmbeddedPDF
                                                key={HRQuestionId.unwrap(q.id)}
                                                pdfUrl={"/pdf/cori-acknowledgement-form.pdf"}
                                                containerElementId={"GenericQuestionGroupListRootId"}
                                            />
                                        );
                                    }
                                    case ("PlaceholderIt21042022Form"): {
                                        return (
                                            <PlaceholderEmbeddedPDF
                                                key={HRQuestionId.unwrap(q.id)}
                                                pdfUrl={"/pdf/it2104-2022.pdf"}
                                                containerElementId={"GenericQuestionGroupListRootId"}
                                            />
                                        );
                                    }
                                    case ("PlaceholderW42022Form"): {
                                        return (
                                            <PlaceholderEmbeddedPDF
                                                key={HRQuestionId.unwrap(q.id)}
                                                pdfUrl={"/pdf/fw4-2022.pdf"}
                                                containerElementId={"GenericQuestionGroupListRootId"}
                                            />
                                        );
                                    }
                                    default: {
                                        return "";
                                    }
                                }
                            }
                            case "TitleText": {
                                return (
                                    <PlaceholderText
                                        key={HRQuestionId.unwrap(q.id)}
                                        isTitle={true}
                                        questionText={q.questionText}
                                    />);
                            }
                            case "PlainText": {
                                const formattedPlainText = q.questionText.replace(/(\r\n|\n|\r)/gm, " ");
                                return (
                                    <PlaceholderFormattedText
                                        key={HRQuestionId.unwrap(q.id)}
                                        questionText={formattedPlainText}
                                    />);
                            }
                            case "DownloadableFile": {
                                return (<PlaceholderDownloadableFile
                                    key={HRQuestionId.unwrap(q.id)}
                                    hrDownloadableFileQuestionWithStatus={{
                                        question: q,
                                        status: question.status
                                    }}
                                    getDownloadableFile={(hrQuestion: HRQuestion) => this.getDownloadableFile(hrQuestion)}
                                    onChange={(newVal, id) => this.props.onAnswerChange(newVal, id)}
                                    onTriggerEvaluateHierarchyStatus={(id) => this.props.onTriggerEvaluateHierarchyStatus(id)}
                                />);
                            }
                            default: {
                                return assertNever(q);
                            }
                        }
                    })}
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/GenericQuestionGroup.tsx